import React from "react";
import { Words, FadeUp, PopIn } from "@/components/Reveal";
import PillButton from "@/components/PillButton";

const ROUTES = [
  {
    title: "Join Stephanie's community",
    text: "The best way to stay in touch. Leave your name and email and we'll be in touch with the occasional community update.",
    cta: { label: "Join the Community", href: "/community" },
  },
  {
    title: "Guest ideas + episode topics",
    text: "Have a testimony to share, or a topic worth a longer conversation? Point us to it — the invite list starts with recommendations.",
    cta: { label: "Send it through the community", href: "/community" },
  },
  {
    title: "Organizations + affiliates",
    text: "Working alongside one of Stephanie's causes and want to explore a conversation? Start with the affiliated-organization page for context, then reach out.",
    cta: { label: "See causes & affiliations", href: "/causes" },
  },
  {
    title: "Membership + billing questions",
    text: "The Free tier is live today; paid tiers launch soon. Any question in the meantime — send it through the community and we'll route it.",
    cta: { label: "See membership plans", href: "/membership" },
  },
];

const Contact = () => (
  <main className="bg-white">
    <section className="w-full bg-white pt-[110px] sm:pt-[130px] lg:pt-[150px]">
      <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-[56px]">
        <div className="mx-auto flex max-w-[1208px] flex-col gap-5 lg:px-[60px]">
          <Words text="Contact" mode="load" delay={0.1} className="eyebrow inline-flex overflow-hidden text-[13px] leading-[22.4px] text-charcoal sm:text-[14px]" />
          <h1 className="font-display text-[40px] leading-[1.08] text-black sm:text-[54px] lg:text-[64px] lg:leading-[70px]" aria-label="Say Hello">
            <Words text="Say Hello" mode="load" delay={0.2} stagger={0.09} />
          </h1>
          <FadeUp mode="load" delay={0.5}>
            <p className="max-w-[620px] text-[15px] leading-[26px] text-charcoal sm:text-[15.6px] sm:leading-[27.2px]">
              One inbox routes through the community. Pick the closest fit — general, podcast, partners, or support — and we’ll go from there.
            </p>
          </FadeUp>
        </div>
      </div>
    </section>

    <section className="w-full bg-white py-14 sm:py-16 lg:py-[72px]">
      <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-[56px]">
        <div className="mx-auto grid max-w-[1208px] grid-cols-1 gap-6 sm:grid-cols-2 lg:px-[60px]">
          {ROUTES.map((r, i) => (
            <FadeUp key={r.title} delay={0.06 * i} y={22} className="h-full">
              <div
                data-testid={`contact-route-${i + 1}`}
                className="flex h-full flex-col gap-4 rounded-3xl border border-hairline bg-white-smoke p-8 transition-all duration-500 hover:-translate-y-1 hover:border-silver hover:bg-white sm:p-10"
              >
                <span className="font-display text-[12px] uppercase tracking-[0.22em] text-charcoal-mid">0{i + 1}</span>
                <h2 className="font-display text-[23px] leading-[1.2] text-black sm:text-[26px]">{r.title}</h2>
                <p className="text-[14.4px] leading-[25px] text-charcoal">{r.text}</p>
                <div className="mt-auto pt-3">
                  <PillButton href={r.cta.href} dataTestId={`contact-cta-${i + 1}`}>
                    {r.cta.label}
                  </PillButton>
                </div>
              </div>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>

    <section className="w-full bg-white-smoke py-16 sm:py-20">
      <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-[56px]">
        <div className="mx-auto flex max-w-[1208px] flex-col items-start gap-8 rounded-3xl bg-ink p-8 sm:p-12 lg:flex-row lg:items-center lg:justify-between lg:px-[60px]">
          <div className="flex max-w-[560px] flex-col gap-3 text-linen">
            <h2 className="font-display text-[28px] leading-[1.15] sm:text-[34px]">
              <Words text="One inbox" />
            </h2>
            <FadeUp y={14}>
              <p className="text-[14.6px] leading-[26px] text-linen/80 sm:text-[15.2px]">
                Everything routes through the community. One form, one place, one small circle. We read what comes in and reply when a reply serves. Nothing
                gets sold, forwarded, or lost.
              </p>
            </FadeUp>
          </div>
          <FadeUp y={12}>
            <PillButton href="/community" dataTestId="contact-one-inbox-button">
              Join the Community
            </PillButton>
          </FadeUp>
        </div>
      </div>
    </section>
  </main>
);

export default Contact;
